import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../../styles/colors';
import { DefaultInput } from '../general/forms/DefaultInput';
import { Icon } from '../general/Icon';
import { GenericList } from '../lists/GenericList';
import { useShowConfirmationPopup } from '../general/ConfirmationPopup';
import { useShowAlertPopup } from '../../state/hooks/AlertHooks';
import { ProductCategory } from '../../interfaces/Product';
import { LinkButton } from '../general/LinkButton';
import axios from 'axios';
import { TextBox } from '../general/forms/TextBox';
import { FileUpload, FileUploadFile } from '../general/forms/FileUpload';
import { DownloadLink } from './ProductForm';
import { CopyIdButton } from '../general/CopyIdButton';

export interface ProductCategoryFormProps {
  category?: ProductCategory | null;
  setProductCategory: (payload: { category: ProductCategory; attachment: FileUploadFile }) => void;
  onCancel?: () => void;
}

export interface ProductCategoryFromValidationState {
  name: boolean;
  description: boolean;
  image: boolean;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${Colors.white};
  padding: 2rem 1rem;
`;

const ToggleInnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  grid-column-start: 1;
  grid-column-end: 4;
  padding: 1.5rem 0 0.5rem;

  a {
    text-overflow: ellipsis;
    width: 100%;
    color: ${Colors.primary};
  }
`;

const Headline = styled.h2<{ divideContent?: boolean }>`
  display: flex;
  flex-direction: row;
  min-height: 1.5rem;
  margin-bottom: 1rem;

  margin-top: ${(props) => (props.divideContent ? '3rem' : '0')};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  background-color: ${Colors.white};
`;

const Fieldset = styled.fieldset`
  display: flex;
  flex-direction: column;
  border: none;
  margin-bottom: 3rem;

  ${Headline} {
    margin-bottom: -0.5rem;
    color: ${Colors.grayDark};
    font-weight: 700;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 1rem;
  justify-content: space-between;
`;

const NoDocumentsTeaser = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
`;

const DeleteAttachmentIconWrapper = styled.div`
  &:hover {
    cursor: pointer;
  }
`;

export const ProductCategoryForm: FC<ProductCategoryFormProps> = (props) => {
  const { category = null, setProductCategory, onCancel } = props;
  const showConfirmationPopup = useShowConfirmationPopup();
  const [tempProductCategory, setTempProductCategory] = useState<ProductCategory | null>(category);
  const [tempAttachment, setTempAttachment] = useState<FileUploadFile | null>(null);
  const [productCategoryAttachment, setProductCategoryAttachment] = useState<FileUploadFile | null>(null);
  const showNotificationBanner = useShowAlertPopup();
  const initialValidationState = {
    name: false,
    description: false,
    image: false,
  };
  const [validationState, setValidationState] = useState<ProductCategoryFromValidationState>(initialValidationState);

  const validate = () => {
    const state = {
      name: tempProductCategory?.name && tempProductCategory?.name.length > 0 ? false : true,
      // description: tempProductCategory?.description && tempProductCategory?.description.length > 0 ? false : true,
      description: false,   // optional
      image: tempAttachment !== null || tempProductCategory?.imageId?.length > 0 ? false : true,
    };
    setValidationState(state);
    return state;
  };

  const fetchAttachment = (id: string) => {
    return axios.get(`/api/attachment/${id}`);
  };

  useEffect(() => {
    if (category && category.imageId) {
      fetchAttachment(category.imageId)
        .then((response) => {
          setProductCategoryAttachment(response.data);
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }, [category]);

  // update tempCustomer on props change
  useEffect(() => setTempProductCategory(category), [category]);

  return (
    <>
      <Headline>Kategorie {category === null ? 'Hinzufügen' : 'Bearbeiten'}</Headline>
      <Wrapper>
        <Form>
          <Fieldset>
            <Headline>Kategorie Stammdaten</Headline>
            <DefaultInput
              id={'name'}
              onChange={(event) => {
                setTempProductCategory({ ...tempProductCategory, name: event.target.value });
              }}
              type={'text'}
              label={'Name'}
              placeholder={'Name der Kategorie'}
              value={tempProductCategory?.name || ''}
              required={true}
              hasError={validationState?.name}
            />
            <TextBox
              id={'description'}
              onChange={(event) => {
                setTempProductCategory({ ...tempProductCategory, description: event.target.value });
              }}
              label={'Beschreibung'}
              placeholder={''}
              value={tempProductCategory?.description || ''}
              required={false}
              hasError={validationState.description}
            />
            <FileUpload
              allowedFileTypes={['image/svg+xml']}
              label={'Teaser'}
              multiFileUpload={false}
              id={'file-upload'}
              required={true}
              hasError={validationState?.image}
              onChange={(files) => {
                setTempAttachment(files[0]);
              }}
            />
          </Fieldset>
        </Form>
      </Wrapper>
      {productCategoryAttachment ? (
        <>
          <Headline divideContent={true}>Dokumente zum Produkt</Headline>
          <GenericList
            columns={[{ name: 'fileName', displayName: 'Dokumente', sortable: false }]}
            collapsible={true}
            items={[productCategoryAttachment]}
            additionalRenderFn={(file: FileUploadFile) => {
              return (
                <>
                  <ToggleInnerWrapper>
                    <DownloadLink
                      onClick={() => {
                        const a = document.createElement("a");
                        a.href = file.dataUrl;
                        a.download = file.fileName;
                        a.click();
                      }}
                    >Anzeigen</DownloadLink>
                    <CopyIdButton id={file.id} />
                    <DeleteAttachmentIconWrapper
                      onClick={() => {
                        showConfirmationPopup({
                          open: true,
                          actionButtonLabel: 'Löschen',
                          closeFn() {
                            showConfirmationPopup(null);
                          },
                          actionFn() {
                            axios
                              .post('/api/product/attachment', {
                                productId: category.id,
                                attachmentId: file.id,
                              })
                              .then((response) => {
                                showNotificationBanner({
                                  renderContent: () => {
                                    return <>Dokument wurde gelöscht.</>;
                                  },
                                });
                                showConfirmationPopup(null);
                                history.go(0);
                              })
                              .catch((error) => {
                                showNotificationBanner({
                                  error: true,
                                  renderContent: () => {
                                    return <>Dokument konnte nicht gelöscht werden.</>;
                                  },
                                });
                                showConfirmationPopup(null);
                              });
                          },
                          renderer() {
                            return <>Wollen Sie das Dokument wirklich löschen?</>;
                          },
                        });
                      }}
                    >
                      <Icon icon={'trash'} size={24} />
                    </DeleteAttachmentIconWrapper>
                  </ToggleInnerWrapper>
                </>
              );
            }}
          />
        </>
      ) : (
        <NoDocumentsTeaser>Teaser können erst angezeigt werden, wenn die Kategorie erstellt wurde!</NoDocumentsTeaser>
      )}
      <ButtonWrapper>
        <LinkButton
          onChangeHandler={(event: React.FormEvent<HTMLButtonElement>) => {
            onCancel && onCancel();
          }}>
          Abbrechen
        </LinkButton>
        <LinkButton
          onChangeHandler={(event: React.FormEvent<HTMLButtonElement>) => {
            const validations = validate();
            const inValid =
              Object.keys(validations)
                .map((key) => {
                  return validations[key];
                })
                .filter(Boolean).length > 0;

            if (!inValid) {
              setProductCategory({ category: tempProductCategory, attachment: tempAttachment });
            } else {
              showNotificationBanner({
                error: true,
                renderContent: () => {
                  return <>Bitte füllen Sie alle Rot markierten Felder aus.</>;
                },
              });
            }
          }}>
          Speichern
        </LinkButton>
      </ButtonWrapper>
    </>
  );
};
